<template>
  <modal title="Payment Method" size="xs" ref="modal">
    <q-img src="/img/stripe.png" class="q-mb-md" />
    <form class="q-gutter-y-md">
      <q-input label="Label (optional)" v-model="label" outlined hint="e.g. Personal, Business" />
      <q-input label="Name on Card" v-model="nameOnCard" outlined />
      <div class="row q-gutter-x-sm">
        <div class="col-8">
          <stripe-element
            type="cardNumber"
            placeholder="Card Number"
            @mounted="cardNoElement = $event"
            @change="onChange"
          />
        </div>
        <div class="col">
          <stripe-element
            type="cardExpiry"
            @change="onChange"
          />
        </div>
      </div>
      <div class="row q-gutter-x-sm">
        <div class="col">
          <stripe-element
            type="cardCvc"
            @change="onChange"
          />
        </div>
        <div class="col">
          <stripe-element
            type="postalCode"
            placeholder="Zip/Postal Code"
            @change="onChange"
          />
        </div>
      </div>
      <message v-if="errorMessage" type="negative">
        {{ errorMessage }}
      </message>
    </form>
    <template v-slot:actions>
      <async-btn label="Save" color="primary" :task="task" @click="save" />
    </template>
  </modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ModalMixin } from '@/mixins';
import { PaymentMethod, Modal as ModalElement } from '@/type';
import Modal from '@/components/modal/Modal.vue';
import Message from '@/components/message/Message.vue';
import AsyncBtn from '@/components/AsyncBtn.vue';
import notify from '@/components/notify';
import { createPaymentMethod } from '@/billing/stripe/stripe';
import StripeElement from '@/billing/stripe/StripeElement.vue';
import { add } from './payment-method';

interface Data {
  label: string,
  nameOnCard: string,
  cardNoElement: object|null,
  task: Promise<PaymentMethod>|null
  errorMessage: string|null,
}

interface ChangeEvent {
  error?: {
    message: string,
  },
}

export default defineComponent({
  emits: ['ok'],
  mixins: [ModalMixin],
  components: {
    Modal,
    StripeElement,
    Message,
    AsyncBtn,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      label: '',
      nameOnCard: '',
      cardNoElement: null,
      task: null,
      errorMessage: null,
    };
  },
  methods: {
    createExternally(): Promise<string> {
      if (this.cardNoElement) {
        return createPaymentMethod(this.nameOnCard, this.cardNoElement);
      }
      return Promise.reject();
    },
    save() {
      this.task = this.createExternally().then(this.add);
      this.task.then((paymentMethod: PaymentMethod) => {
        this.$emit('ok', paymentMethod);
        (this.$refs.modal as ModalElement).hide();
        notify('Payment method saved.');
      });
    },
    add(paymentMethodId: string): Promise<PaymentMethod> {
      const label = (this.label || '').trim() || null;
      return add(this.customerId, paymentMethodId, label);
    },
    onChange(event: ChangeEvent) {
      let error: string|null = null;
      if (event.error) {
        error = event.error.message;
      }
      this.errorMessage = error;
    },
  },
});
</script>
